import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import { createTooltipProps } from '../utils/fieldTooltips';
import { ValidationRules, validateForm, validateField, hasErrors } from '../utils/validation';

const INSPECTION_TEST_SYSTEM_FIELDS = new Set([
  'id',
  'created_at',
  'updated_at',
  'tenant_id',
  'user_id',
]);

function appendCopySuffix(value, maxLength) {
  const suffix = ' (Copy)';
  const base = String(value || '').trim() || 'Record';
  if (base.length + suffix.length <= maxLength) {
    return `${base}${suffix}`;
  }
  return `${base.slice(0, maxLength - suffix.length)}${suffix}`;
}

function buildInspectionTestCopyPayload(inspectionTest) {
  const copy = buildInspectionTestUpdatePayload(inspectionTest);
  copy.item_name = appendCopySuffix(copy.item_name, 100);
  return copy;
}

function buildInspectionTestUpdatePayload(row, overrides = {}) {
  const payload = {};
  for (const [key, value] of Object.entries(row)) {
    if (!INSPECTION_TEST_SYSTEM_FIELDS.has(key)) {
      payload[key] = value;
    }
  }
  return { ...payload, ...overrides };
}

function EditableTextCell({
  row,
  field,
  editingCell,
  editingValue,
  savingCell,
  onStartEdit,
  onChange,
  onSave,
  onKeyDown,
  maxLength,
  multiline = false,
  emphasize = false,
  cellStyle,
}) {
  const cellKey = `${row.id}-${field}`;
  const isEditing = editingCell === cellKey;
  const isSaving = savingCell === cellKey;
  const displayValue = row[field] || '—';

  if (isEditing) {
    return (
      <Form.Control
        as={multiline ? 'textarea' : undefined}
        type={multiline ? undefined : 'text'}
        rows={multiline ? 2 : undefined}
        size="sm"
        value={editingValue}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onSave(row, field)}
        onKeyDown={(e) => onKeyDown(e, row, field, multiline)}
        autoFocus
        disabled={isSaving}
        style={{ minWidth: multiline ? '180px' : '120px', ...cellStyle }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      className={emphasize ? 'fw-semibold' : undefined}
      style={{
        cursor: 'pointer',
        display: 'inline-block',
        minWidth: '48px',
        maxWidth: multiline ? '200px' : undefined,
        wordWrap: 'break-word',
        ...cellStyle,
      }}
      onClick={() => onStartEdit(row, field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(row, field);
        }
      }}
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

function InspectionTestModal({ inspectionTest, onClose, onSave }) {
  const [formData, setFormData] = useState({
    inspection_name: '',
    expiry_days: '',
    print_on_confirm: false,
    raise_job: false,
    item_name: '',
    check_for: '',
    requires_value: false,
    low_value: '',
    high_value: '',
    value_units: '',
    print: false,
    help_notes: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (inspectionTest) {
      setFormData(inspectionTest);
    }
  }, [inspectionTest]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const newFormData = {
      ...formData,
      [e.target.name]: value,
    };
    setFormData(newFormData);

    // Clear validation errors for this field when user starts typing
    if (validationErrors[e.target.name]) {
      const newErrors = { ...validationErrors };
      delete newErrors[e.target.name];
      setValidationErrors(newErrors);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form data
    const errors = validateForm(formData, ValidationRules.inspectionTest);

    if (hasErrors(errors)) {
      setValidationErrors(errors);
      return;
    }

    // Clear any existing errors
    setValidationErrors({});
    onSave(formData);
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {inspectionTest ? 'Edit Inspection Test' : 'Add New Inspection Test'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Container fluid>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Inspection Name *</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('inspection_name')}>
                        Used to define standard inspection and testing procedures
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="text"
                      name="inspection_name"
                      value={formData.inspection_name}
                      onChange={handleChange}
                      maxLength="60"
                      required
                      isInvalid={!!validationErrors.inspection_name}
                      {...createTooltipProps('inspection_name')}
                    />
                  </OverlayTrigger>
                  {validationErrors.inspection_name && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.inspection_name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Expiry Days</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('expiry_days')}>
                        Defines how often the test should be performed (in days)
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="number"
                      name="expiry_days"
                      value={formData.expiry_days}
                      onChange={handleChange}
                      min="0"
                      {...createTooltipProps('expiry_days')}
                    />
                  </OverlayTrigger>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Item Name *</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('item_name')}>
                        Used for detailed specifications and maintenance
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="text"
                      name="item_name"
                      value={formData.item_name}
                      onChange={handleChange}
                      maxLength="100"
                      required
                      isInvalid={!!validationErrors.item_name}
                      {...createTooltipProps('item_name')}
                    />
                  </OverlayTrigger>
                  {validationErrors.item_name && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.item_name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Check For</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('check_for')}>
                        Provides detailed information about how the test should be performed
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="check_for"
                      value={formData.check_for}
                      onChange={handleChange}
                      maxLength="200"
                      {...createTooltipProps('check_for')}
                    />
                  </OverlayTrigger>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Low Value</Form.Label>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip {...createTooltipProps('low_value')}>
                            Minimum acceptable value for the test measurement
                          </Tooltip>
                        }
                      >
                        <Form.Control
                          type="number"
                          name="low_value"
                          value={formData.low_value}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('low_value')}
                        />
                      </OverlayTrigger>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>High Value</Form.Label>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip {...createTooltipProps('high_value')}>
                            Maximum acceptable value for the test measurement
                          </Tooltip>
                        }
                      >
                        <Form.Control
                          type="number"
                          name="high_value"
                          value={formData.high_value}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('high_value')}
                        />
                      </OverlayTrigger>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Value Units</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('value_units')}>
                        Units of measurement for test values (e.g., Hours, PSI, °C)
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="text"
                      name="value_units"
                      value={formData.value_units}
                      onChange={handleChange}
                      maxLength="20"
                      placeholder="e.g., Hours, PSI, °C"
                      {...createTooltipProps('value_units')}
                    />
                  </OverlayTrigger>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Help Notes</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('help_notes')}>
                        Provides additional guidance for performing the test
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="help_notes"
                      value={formData.help_notes}
                      onChange={handleChange}
                      maxLength="500"
                      {...createTooltipProps('help_notes')}
                    />
                  </OverlayTrigger>
                </Form.Group>

                <Row className="mb-3">
                  <Col md={6}>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('print_on_confirm')}>
                          Controls automatic printing of test results
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="print_on_confirm"
                        label="Print on Confirm"
                        checked={formData.print_on_confirm}
                        onChange={handleChange}
                        {...createTooltipProps('print_on_confirm')}
                      />
                    </OverlayTrigger>
                  </Col>

                  <Col md={6}>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('raise_job')}>
                          Determines if maintenance jobs should be created for failed tests
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="raise_job"
                        label="Raise Job"
                        checked={formData.raise_job}
                        onChange={handleChange}
                        {...createTooltipProps('raise_job')}
                      />
                    </OverlayTrigger>
                  </Col>

                  <Col md={6}>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('requires_value')}>
                          Determines if the test requires numerical measurement
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="requires_value"
                        label="Requires Value"
                        checked={formData.requires_value}
                        onChange={handleChange}
                        {...createTooltipProps('requires_value')}
                      />
                    </OverlayTrigger>
                  </Col>

                  <Col md={6}>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('print')}>
                          Controls whether test results are printed automatically
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="print"
                        label="Print"
                        checked={formData.print}
                        onChange={handleChange}
                        {...createTooltipProps('print')}
                      />
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {inspectionTest ? 'Update' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function InspectionTests() {
  const [inspectionTests, setInspectionTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInspectionTest, setEditingInspectionTest] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchInspectionTests = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inspection-tests', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setInspectionTests(response.data.inspectionTests);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch inspection tests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInspectionTests();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInspectionTests(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchInspectionTests(newPage, searchTerm);
  };

  const handleAddInspectionTest = () => {
    setEditingInspectionTest(null);
    setShowModal(true);
  };

  const handleEditInspectionTest = (inspectionTest) => {
    setEditingInspectionTest(inspectionTest);
    setShowModal(true);
  };

  const handleSaveInspectionTest = async (inspectionTestData) => {
    try {
      if (editingInspectionTest) {
        await axios.put(`/api/inspection-tests/${editingInspectionTest.id}`, inspectionTestData);
        setSuccess('Inspection test updated successfully');
      } else {
        await axios.post('/api/inspection-tests', inspectionTestData);
        setSuccess('Inspection test created successfully');
      }
      setShowModal(false);
      fetchInspectionTests(pagination.page, searchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save inspection test';
      setError(errorMessage);

      // If it's a validation error, don't close the modal so user can fix it
      if (
        errorMessage.toLowerCase().includes('missing required') ||
        errorMessage.toLowerCase().includes('must be')
      ) {
        // Keep modal open for validation errors
      } else {
        setShowModal(false);
      }
    }
  };

  const handleDeleteInspectionTest = async (id) => {
    if (window.confirm('Are you sure you want to delete this inspection test?')) {
      try {
        await axios.delete(`/api/inspection-tests/${id}`);
        setSuccess('Inspection test deleted successfully');
        fetchInspectionTests(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete inspection test');
      }
    }
  };

  const handleDeleteAllInspectionTests = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL inspection tests? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your inspection test records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/inspection-tests');
          setSuccess(
            `All inspection tests deleted successfully (${response.data.deletedCount} records)`
          );
          fetchInspectionTests(1, '');
        } catch (error) {
          setError('Failed to delete all inspection tests');
        }
      }
    }
  };

  const startTextEdit = (row, field) => {
    setEditingCell(`${row.id}-${field}`);
    setEditingValue(row[field] == null ? '' : String(row[field]));
  };

  const cancelTextEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveTextEdit = async (row, field) => {
    const cellKey = `${row.id}-${field}`;
    if (editingCell !== cellKey) {
      return;
    }

    const raw = editingValue.trim();
    const current = row[field] == null ? '' : String(row[field]).trim();

    if (raw === current) {
      cancelTextEdit();
      return;
    }

    const fieldError = validateField(field, raw, ValidationRules.inspectionTest[field], row);
    if (fieldError) {
      setError(fieldError);
      cancelTextEdit();
      return;
    }

    setSavingCell(cellKey);
    try {
      const response = await axios.put(
        `/api/inspection-tests/${row.id}`,
        buildInspectionTestUpdatePayload(row, {
          [field]: raw || null,
        })
      );
      setInspectionTests((prev) =>
        prev.map((test) => (test.id === row.id ? { ...test, ...response.data } : test))
      );
      const labels = {
        item_name: 'Item name',
        check_for: 'Check for',
      };
      setSuccess(`${labels[field] || 'Field'} updated`);
      setError('');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update inspection test');
    } finally {
      setSavingCell(null);
      cancelTextEdit();
    }
  };

  const handleTextKeyDown = (e, row, field, multiline = false) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      saveTextEdit(row, field);
    } else if (e.key === 'Escape') {
      cancelTextEdit();
    }
  };

  const handleCopyInspectionTest = async (inspectionTest) => {
    setCopyingId(inspectionTest.id);
    setError('');
    try {
      await wrapCopy('Copying inspection test...', 1, async (tick) => {
        await axios.post('/api/inspection-tests', buildInspectionTestCopyPayload(inspectionTest));
        tick({ success: true });
      });
      setSuccess('Inspection test copied successfully');
      fetchInspectionTests(pagination.page, searchTerm);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to copy inspection test');
    } finally {
      setCopyingId(null);
    }
  };

  const renderBooleanIcon = (value) => {
    return value ? (
      <span style={{ color: '#28a745' }}>✓</span>
    ) : (
      <span style={{ color: '#6c757d' }}>✗</span>
    );
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                <i
                  className="fas fa-clipboard-check me-3 text-success"
                  style={{ fontSize: '2.5rem' }}
                ></i>
                <div>
                  <h1 className="mb-0">Inspection Tests</h1>
                  <p className="text-muted mb-0">
                    Configure inspection tests and quality control procedures
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Button variant="primary" onClick={handleAddInspectionTest} className="me-2">
                Add Inspection Test
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAllInspectionTests}
                disabled={inspectionTests.length === 0}
              >
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {copyProgress && (
        <Row className="mb-3">
          <Col>
            <OperationProgress
              title={copyProgress.title}
              processed={copyProgress.processed}
              total={copyProgress.total}
              success={copyProgress.success}
              failed={copyProgress.failed}
              variant="info"
            />
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by inspection name, item name, or check for..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading inspection tests...</div>
          ) : inspectionTests.length === 0 ? (
            <div className="text-center p-3">No inspection tests found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                  <thead>
                    <tr>
                      <th>Inspection Name</th>
                      <th>Item Name</th>
                      <th>Check For</th>
                      <th>Expiry Days</th>
                      <th>Value Range</th>
                      <th>Print</th>
                      <th>Raise Job</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspectionTests.map((test) => (
                      <tr key={test.id}>
                        <td className="name">
                          <strong>{test.inspection_name}</strong>
                        </td>
                        <td className="name">
                          <EditableTextCell
                            row={test}
                            field="item_name"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startTextEdit}
                            onChange={setEditingValue}
                            onSave={saveTextEdit}
                            onKeyDown={handleTextKeyDown}
                            maxLength={100}
                          />
                        </td>
                        <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                          <EditableTextCell
                            row={test}
                            field="check_for"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startTextEdit}
                            onChange={setEditingValue}
                            onSave={saveTextEdit}
                            onKeyDown={handleTextKeyDown}
                            maxLength={200}
                            multiline
                          />
                        </td>
                        <td>{test.expiry_days || '-'}</td>
                        <td>
                          {test.requires_value && (test.low_value || test.high_value) ? (
                            <span>
                              {test.low_value || '0'} - {test.high_value || '∞'}
                              {test.value_units && ` ${test.value_units}`}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <Badge bg={test.print ? 'success' : 'secondary'}>
                            {test.print ? '✓' : '✗'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={test.raise_job ? 'success' : 'secondary'}>
                            {test.raise_job ? '✓' : '✗'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditInspectionTest(test)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopyInspectionTest(test)}
                              disabled={copyingId === test.id}
                            >
                              {copyingId === test.id ? 'Copying...' : 'Copy'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteInspectionTest(test.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <InspectionTestModal
          inspectionTest={editingInspectionTest}
          onClose={() => setShowModal(false)}
          onSave={handleSaveInspectionTest}
        />
      )}
    </Container>
  );
}

export default InspectionTests;
